import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Clock, XCircle } from "lucide-react";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setState("paid"); return; }
        throw new Error("pending");
      } catch {
        if (cancelled) return;
        attempts.current += 1;
        if (attempts.current < 20) setTimeout(tick, 2000);
        else setState("pending");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-[70vh] bg-secondary flex items-center justify-center px-5 pt-28 pb-16">
      <div className="max-w-lg w-full border border-border bg-card p-10 md:p-12 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="text-brand-orange mx-auto mb-6 animate-spin" />
            <h1 className="font-display font-black text-3xl mb-3 tracking-tight">Confirming payment...</h1>
            <p className="text-muted-foreground">Please wait while we confirm your FastTrack payment with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-green-600 mx-auto mb-6" />
            <h1 className="font-display font-black text-3xl mb-3 tracking-tight" data-testid="payment-confirmed">Payment Confirmed!</h1>
            <p className="text-muted-foreground mb-2">Your <span className="font-semibold text-foreground">SourceX FastTrack™</span> priority request is now <span className="font-semibold text-red-600">active</span>.</p>
            <p className="text-muted-foreground mb-8">Our dedicated sourcing team has been alerted and will contact you on priority. A receipt has been emailed to you by Stripe.</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-brand-orange text-white px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-brand-orangeHover transition-colors">Back to Home</Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Clock size={48} className="text-brand-orange mx-auto mb-6" />
            <h1 className="font-display font-black text-3xl mb-3 tracking-tight">Payment Processing</h1>
            <p className="text-muted-foreground mb-8">Your payment is being processed. This can take a few minutes — your FastTrack request is saved and will activate automatically once confirmed.</p>
            <Link to="/" className="inline-flex items-center gap-2 border border-foreground px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors">Back to Home</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={48} className="text-red-600 mx-auto mb-6" />
            <h1 className="font-display font-black text-3xl mb-3 tracking-tight">Couldn't Verify Payment</h1>
            <p className="text-muted-foreground mb-8">We couldn't find this payment session. If you completed a payment, don't worry — contact us on WhatsApp and we'll confirm it manually.</p>
            <Link to="/" className="inline-flex items-center gap-2 border border-foreground px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors">Back to Home</Link>
          </>
        )}
      </div>
    </div>
  );
}
